/**
 * Which model answers, which service listens and speaks — and whose decision
 * each of those is.
 *
 * Two decisions live here and they are kept apart on purpose, because they are
 * not the same decision: a family might want Claude answering and the browser's
 * own free voice, or a local model on their own machine and a good voice from
 * elsewhere. Neither choice constrains the other.
 *
 * ## Two homes, for two different kinds of setting
 *
 * **The family's** — who answers, which model, the key, the voice service —
 * lives on the family record in the database (`AssistantConfig`), set once by an
 * administrator and true for everybody. It has to work that way: the person this
 * assistant exists for is never going to open Admin controls and type an API
 * key, and a setting each browser holds separately is a setting that is wrong
 * for almost everyone. Keeping it with the data also means it survives anything
 * that clears a browser's local storage, which is how the first version of this
 * quietly lost a key that had been carefully typed in the day before.
 *
 * **The person's** — do I want to talk to it, do I want it read aloud — stays in
 * this browser. Those are about the room you are sitting in, not about the
 * family, and they should not follow you onto somebody else's device.
 *
 * ## A word about the key
 *
 * On the family record, the key is readable by every signed-in member's browser,
 * because every member's browser is what makes the call. That is the honest cost
 * of "set it once for everyone" in an app with no server. Point the family at
 * `proxy` — an address you run — and the key never leaves your server instead.
 *
 * ## The app's own server comes first
 *
 * When the app is served by its own server (`server/` — the dev server on this
 * computer, or the hosted site), that server holds the keys and reports what it
 * can do (`server`, below). It then answers, listens and speaks for everybody on
 * every device, and the per-browser keys above are not used at all. That is the
 * set-up nobody has to set up: the phone on the Wi-Fi and the grandmother in
 * another country get the same assistant as the administrator's laptop.
 */
import type { Lang } from '../domain/types';
import type { ServerStatus } from './providers/serverContract';

export type ProviderId = 'local' | 'anthropic' | 'openai' | 'proxy';
/** Who listens. Speech-to-text only. `server` is the app's own server. */
export type ListenId = 'off' | 'browser' | 'openai' | 'proxy' | 'server';
/** Who speaks. Speechify is here and not above because it does not do listening. */
export type SpeakId = 'off' | 'browser' | 'openai' | 'speechify' | 'proxy' | 'server';

/** The family's half: set by an administrator, shared by everybody. */
export const SHARED_KEYS = [
  'provider', 'model', 'apiKey', 'baseUrl',
  'listenWith', 'listenKey', 'listenUrl',
  'speakWith', 'speakKey', 'speakUrl', 'voiceName',
  'effort', 'listenModel', 'speakModel',
] as const;
export type SharedKey = (typeof SHARED_KEYS)[number];

export interface AssistantSettings {
  /** Who answers. `local` needs nothing and always works. */
  provider: ProviderId;
  model: string;
  /** Held in this browser only. Empty when `provider` is `local` or `proxy`. */
  apiKey: string;
  /** For `proxy`, and for `openai` when pointing at an OpenAI-compatible server. */
  baseUrl: string;
  /**
   * Who listens, and who speaks — two settings, because they are two services.
   *
   * They were one until Speechify arrived, which does speaking and not
   * listening: a family that wants its warmer voice still needs the browser or
   * Whisper doing the hearing. Splitting them also turns out to be the better
   * arrangement anyway — the browser listens for free and privately, and paying
   * for a good voice is the half that is actually worth paying for.
   */
  listenWith: ListenId;
  listenKey: string;
  listenUrl: string;
  speakWith: SpeakId;
  speakKey: string;
  speakUrl: string;
  /** Which voice, when the speaking service offers a choice. */
  voiceName: string;
  /*
   * The family's choices for the app's own server, each empty for "the server's
   * default". `model` above is the answering model; these are the rest. The
   * server only honours a choice its keys can use.
   */
  /** How long the model thinks before answering: `quick`, `balanced` or `careful`. */
  effort: string;
  listenModel: string;
  /** An OpenAI speech model, or `speechify`. */
  speakModel: string;
  /**
   * Read answers and walkthrough steps out loud.
   *
   * On by default. The assistant is built to be used without reading anything,
   * and a voice that has to be switched on first is a voice most people never
   * hear. Turned off in Settings by anyone who would rather it stayed quiet.
   */
  speakAnswers: boolean;
  /** Show the microphone in the panel. */
  listen: boolean;
  /**
   * What the app's own server can do, as it said when asked — or `null` when
   * there is no server (a plain static host, or it could not be reached).
   * Never saved anywhere: it is asked again on every load.
   */
  server: ServerStatus | null;
}

export const DEFAULT_MODELS: Record<ProviderId, string> = {
  local: '',
  anthropic: 'claude-sonnet-5',
  openai: 'gpt-4o-mini',
  proxy: '',
};

export const DEFAULT_SETTINGS: AssistantSettings = {
  provider: 'local',
  model: '',
  apiKey: '',
  baseUrl: '',
  listenWith: 'browser',
  listenKey: '',
  listenUrl: '',
  speakWith: 'browser',
  speakKey: '',
  speakUrl: '',
  voiceName: '',
  effort: '',
  listenModel: '',
  speakModel: '',
  speakAnswers: true,
  listen: true,
  server: null,
};

/** Only the personal half is kept here; the rest comes from the family record. */
const KEY = 'edry.assistant';

/**
 * Settings live outside React because a provider is built inside an async loop,
 * long after the component that started it may have re-rendered. Read straight
 * from storage, write through `saveSettings`, tell anyone who cares.
 */
let cached: AssistantSettings | null = null;
const listeners = new Set<(s: AssistantSettings) => void>();

export function loadSettings(): AssistantSettings {
  if (cached) return cached;
  let stored: Partial<AssistantSettings> = {};
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) stored = JSON.parse(raw) as Partial<AssistantSettings>;
  } catch {
    /* unreadable storage: the defaults are a working assistant, so carry on */
  }
  // Anything shared that an old build left behind here is ignored: the family
  // record is the only authority for those now.
  cached = { ...DEFAULT_SETTINGS, ...personalOnly(stored) };
  return cached;
}

function personalOnly(v: Partial<AssistantSettings>): Partial<AssistantSettings> {
  const out: Partial<AssistantSettings> = {};
  if (typeof v.speakAnswers === 'boolean') out.speakAnswers = v.speakAnswers;
  if (typeof v.listen === 'boolean') out.listen = v.listen;
  return out;
}

/** A personal preference, kept on this device. */
export function saveSettings(patch: Partial<AssistantSettings>): AssistantSettings {
  const next = { ...loadSettings(), ...patch };
  cached = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(personalOnly(next)));
  } catch {
    /* storage full or blocked: keep it for this session at least */
  }
  for (const l of listeners) l(next);
  return next;
}

/**
 * The family's half, as it arrived from the database.
 *
 * Called whenever the family data loads or changes, so an administrator setting
 * a key on one device reaches every other device the moment it syncs — with no
 * reload, and with nothing for anyone else to type.
 */
export function applySharedConfig(config: Partial<Record<SharedKey, string>> | undefined): AssistantSettings {
  const base = loadSettings();
  if (!config) return base;
  const shared: Partial<AssistantSettings> = {};
  for (const k of SHARED_KEYS) {
    const v = config[k];
    if (typeof v !== 'string') continue;
    if (k === 'provider') shared.provider = v as ProviderId;
    else if (k === 'listenWith') shared.listenWith = v as ListenId;
    else if (k === 'speakWith') shared.speakWith = v as SpeakId;
    else shared[k] = v;
  }
  /*
   * A family set up before listening and speaking were told apart kept one
   * `voice` setting for both. Read it into both halves rather than making an
   * administrator type the key in again — which is exactly the annoyance this
   * whole move to the family record was meant to end.
   */
  const legacy = config as Record<string, unknown>;
  if (typeof legacy.voice === 'string' && !config.listenWith && !config.speakWith) {
    const v = legacy.voice;
    shared.listenWith = (v === 'speechify' ? 'browser' : v) as ListenId;
    shared.speakWith = v as SpeakId;
  }
  if (typeof legacy.voiceApiKey === 'string' && !config.listenKey && !config.speakKey) {
    shared.listenKey = legacy.voiceApiKey;
    shared.speakKey = legacy.voiceApiKey;
  }
  if (typeof legacy.voiceBaseUrl === 'string' && !config.listenUrl && !config.speakUrl) {
    shared.listenUrl = legacy.voiceBaseUrl;
    shared.speakUrl = legacy.voiceBaseUrl;
  }
  const next = { ...base, ...shared };
  // Nothing to tell anybody if the family's settings have not actually moved.
  if (SHARED_KEYS.every((k) => base[k] === next[k])) return base;
  cached = next;
  for (const l of listeners) l(next);
  return next;
}

/** The family's half, for writing back to the database. */
export function sharedPart(s: AssistantSettings): Record<SharedKey, string> {
  return {
    provider: s.provider, model: s.model, apiKey: s.apiKey, baseUrl: s.baseUrl,
    listenWith: s.listenWith, listenKey: s.listenKey, listenUrl: s.listenUrl,
    speakWith: s.speakWith, speakKey: s.speakKey, speakUrl: s.speakUrl, voiceName: s.voiceName,
    effort: s.effort, listenModel: s.listenModel, speakModel: s.speakModel,
  };
}

/** What the app's own server reported. Tells everyone listening when it changes. */
export function applyServerStatus(server: ServerStatus | null): AssistantSettings {
  const base = loadSettings();
  if (JSON.stringify(base.server) === JSON.stringify(server)) return base;
  const next = { ...base, server };
  cached = next;
  for (const l of listeners) l(next);
  return next;
}

/**
 * Who is really answering, for the word in the corner of the Help screen.
 *
 * The family record may still say `local` — nobody chose anything — while the
 * server answers with Claude. The corner has to say Claude, or a working model
 * looks exactly like the built-in matcher.
 */
export function answeredBy(s: AssistantSettings): ProviderId {
  return serverAnswer(s)?.provider ?? s.provider;
}

/**
 * Which model the app's server will answer with, as far as the app can tell:
 * the family's choice when the server has a key for its company, the server's
 * default otherwise. (The server has the last word — it also refuses models its
 * key cannot use — but this is right for every model the Admin screen offers.)
 */
export function serverAnswer(s: AssistantSettings): { provider: 'anthropic' | 'openai'; model: string } | null {
  const a = s.server?.answer;
  if (!a) return null;
  const want = s.model.trim();
  if (!want) return { provider: a.provider, model: a.model };
  const provider = want.startsWith('claude-') ? 'anthropic' : 'openai';
  return (a.providers ?? [a.provider]).includes(provider) ? { provider, model: want } : { provider: a.provider, model: a.model };
}

export function onSettings(listener: (s: AssistantSettings) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The model to send, falling back to the provider's usual one. */
export function modelFor(s: AssistantSettings): string {
  return s.model.trim() || DEFAULT_MODELS[s.provider];
}

/**
 * Whether a real model is answering, or the built-in fallback is.
 *
 * Worth a sentence: the fallback is not an error state. With nothing configured
 * at all the button still opens, still recognises the two dozen things people
 * actually ask, and still walks them through it — it just cannot answer anything
 * it has not been taught. Which means the assistant is never a dead end, and a
 * family can use this without an account anywhere.
 */
export function isConfigured(s: AssistantSettings): boolean {
  if (s.server?.answer) return true;
  if (s.provider === 'local') return false;
  if (s.provider === 'proxy') return !!s.baseUrl.trim();
  return !!s.apiKey.trim();
}

/** A short line naming what is set up, for the settings screen. */
export function describeSetup(s: AssistantSettings, lang: Lang): string {
  const model = modelFor(s);
  const fromServer = serverAnswer(s);
  if (fromServer) return fromServer.model;
  if (s.provider === 'local') return lang === 'he' ? 'מובנה — עונה בלי חיבור חיצוני' : 'Built in — answers with no outside connection';
  if (s.provider === 'proxy') return s.baseUrl.trim() || (lang === 'he' ? 'חסרה כתובת' : 'No address yet');
  return model;
}
